/**
 * AcceptJsKeyedEntry — issue #522
 *
 * The desk "card in hand, no reader" flow on the Authorize.net rail:
 * staff hand-keys the walk-in's card, Accept.js tokenizes it IN THE
 * BROWSER into a single-use opaque nonce, and the nonce goes to
 * POST /api/v2/admin/payments/manual/charge (chargeManualNonce — the
 * tested one-shot AUTHCAPTURE server half). The PAN/CVC exist only in
 * component state and the dispatchData call — they are NEVER posted to
 * our API (a test scans every request body to prove it). Card-not-
 * present rates apply and are disclosed on the form.
 *
 * Money-path posture:
 *   - The server derives the charged amount from the booking row + tip.
 *     `totalCents` is what this form DISPLAYS, and it rides along as
 *     `expectedTotalCents` — a cross-check the server compares and
 *     refuses on, never the amount it charges (#522 review / the #490
 *     pattern). A redemption applied on screen but absent from the
 *     booking row now fails closed instead of overcharging.
 *   - A FRESH idempotencyKey is minted per submit-click. This
 *     deliberately differs from the per-mount contract in
 *     lib/idempotency.ts: every submit carries a fresh single-use
 *     nonce anyway, and the server's cross-key pending guard +
 *     already-paid guard own retry safety — a surviving stub from an
 *     ambiguous attempt 409s `charge_unresolved` instead of double
 *     charging (#251 residual 3 / PR #260 SHOULD-FIX 6).
 *   - Ambiguous outcomes (charge_unresolved / charge_duplicate /
 *     charge_indeterminate / transport failure mid-POST) render a
 *     TERMINAL panel with no re-key affordance: staff reconcile in the
 *     Transactions tab first. A definitive decline stays inline and
 *     retryable — the form re-arms EMPTY (PAN hygiene: card data is
 *     wiped the moment tokenization hands it to Accept.js).
 *
 * Component states (DESIGN-PHILOSOPHY five-states rule):
 *   loading         → gateway-config read in flight (no form flash)
 *   error           → config read failed (what happened + Retry)
 *   not-configured  → teaching copy pointing at Gateway credentials
 *   ideal           → the keyed-entry form
 *   charge outcomes → inline retryable error vs terminal no-re-key panel
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminPaymentsApi,
  asAdminAcceptJsConfig,
} from '../../lib/api.js';
import { makeIdempotencyKey } from '../../lib/idempotency.js';
import {
  AcceptJsDispatchError,
  describeAcceptJsError,
  dispatchCardData,
  loadAcceptJs,
  type AcceptJsErrorInfo,
  type AcceptStatic,
} from '../../lib/acceptjs.js';

type AcceptJsKeyedEntryProps = {
  readonly apiUrl: string;
  readonly bookingId: string;
  readonly totalCents: number;
  readonly tipCents: number;
  readonly onSuccess: (paymentId: string) => void;
  readonly onCancel: () => void;
};

type KeyedGatewayBootstrap = {
  apiLoginID: string;
  publicClientKey: string;
  mode: 'sandbox' | 'production';
};

type ConfigState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'not-configured' }
  | { kind: 'ready'; bootstrap: KeyedGatewayBootstrap };

/**
 * Terminal (no-re-key) charge outcomes. Deliberately NO resubmit
 * affordance on any of them — the money may have moved; the recovery is
 * the Transactions tab / gateway dashboard, not another keying.
 */
type TerminalPanel = {
  kind:
    | 'unresolved'
    | 'duplicate'
    | 'indeterminate'
    | 'already-paid'
    | 'unavailable'
    | 'held';
  message: string;
  /** Live gateway transaction id for a HELD auth — staff need it to resolve the review queue. */
  heldTransactionId?: string;
};

type FlowState =
  | { kind: 'idle' }
  | { kind: 'processing' }
  | { kind: 'terminal'; panel: TerminalPanel };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Group card digits 4-4-4-… for display; max 19 digits (longest PAN). */
function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  return digits.length >= 3 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

function terminalTitle(kind: TerminalPanel['kind']): string {
  switch (kind) {
    case 'unresolved':
      return 'Do not charge again yet';
    case 'duplicate':
      return 'Possible duplicate charge';
    case 'indeterminate':
      return 'Charge outcome unknown';
    case 'already-paid':
      return 'Already paid';
    case 'unavailable':
      return 'Card payment unavailable';
    case 'held':
      return 'Card held for review — do not charge again';
  }
}

/**
 * Map a /manual/charge failure to the surface it deserves. Branches on
 * the server's machine `code` (desk body convention: human sentence in
 * `error`, token in `code`); a transport-level failure carries NO code —
 * and on a money POST that means the charge MAY have reached the
 * gateway, so it maps to the indeterminate terminal, never to a retry.
 */
function classifyChargeFailure(result: {
  error?: string;
  code?: string;
  gatewayCode?: string;
  heldTransactionId?: string;
}): { kind: 'inline'; message: string } | { kind: 'terminal'; panel: TerminalPanel } {
  const message = result.error ?? 'The charge could not be completed.';
  switch (result.code) {
    case 'charge_declined':
      // #522 review — a HELD-for-review auth arrives as a decline but is
      // a LIVE transaction holding the customer's funds (AVS/fraud filter
      // on a card-not-present keying is exactly when this fires). Telling
      // staff "no charge went through — try again" invites a second
      // capture that settles alongside the first once the merchant
      // approves the held one. Terminal, and surface the id: the desk's
      // pending stub was released, so this id is the only handle left.
      if (result.gatewayCode === 'held_for_review' || result.heldTransactionId) {
        return {
          kind: 'terminal',
          panel: {
            kind: 'held',
            message:
              'This card was placed on HOLD for review — it is a live authorization, not a decline. Do NOT key the card again. Resolve it in the Authorize.net review queue first; approving it there collects this payment.',
            ...(result.heldTransactionId
              ? { heldTransactionId: result.heldTransactionId }
              : {}),
          },
        };
      }
      return {
        kind: 'inline',
        message: `${message} No charge went through — you can try the card again or use another payment method.`,
      };
    case 'charge_unresolved':
    case 'desk_charge_failed':
      return {
        kind: 'terminal',
        panel: {
          kind: 'unresolved',
          message:
            'A previous attempt for this booking is unresolved — the card may already have been charged. Do NOT key the card again. Check the Transactions tab to reconcile it first.',
        },
      };
    case 'charge_duplicate':
      return { kind: 'terminal', panel: { kind: 'duplicate', message } };
    case 'charge_indeterminate':
      return { kind: 'terminal', panel: { kind: 'indeterminate', message } };
    case 'booking_already_paid':
      return {
        kind: 'terminal',
        panel: {
          kind: 'already-paid',
          message:
            'This booking already has a completed payment — keying the card again would collect twice. If this really is an extra charge (add-ons, split tender), settle it from the booking instead.',
        },
      };
    case 'payments_unavailable':
    case 'desk_charge_unsupported':
    case 'gateway_unsupported':
      return { kind: 'terminal', panel: { kind: 'unavailable', message } };
    case undefined:
      // No machine code ⇒ the wire failed (timeout / network / opaque
      // 5xx). The POST may have reached the server and charged — same
      // no-retry posture as charge_indeterminate.
      return {
        kind: 'terminal',
        panel: {
          kind: 'indeterminate',
          message:
            'We could not confirm whether this charge went through. Do NOT key the card again — check the Transactions tab first.',
        },
      };
    default:
      // Typed but definitive server answers (validation, key reuse,
      // booking_not_found, gateway_error …) — inline, staff decides.
      return { kind: 'inline', message };
  }
}

export function AcceptJsKeyedEntry({
  apiUrl,
  bookingId,
  totalCents,
  tipCents,
  onSuccess,
  onCancel,
}: AcceptJsKeyedEntryProps): JSX.Element {
  const [config, setConfig] = useState<ConfigState>({ kind: 'loading' });
  const [flow, setFlow] = useState<FlowState>({ kind: 'idle' });
  /** Inline, recoverable error (decline / field validation / transient). */
  const [formError, setFormError] = useState<AcceptJsErrorInfo | null>(null);

  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [name, setName] = useState('');
  const [zip, setZip] = useState('');

  const loadConfig = useCallback(async () => {
    setConfig({ kind: 'loading' });
    const result = await adminPaymentsApi.getGatewayConfig(apiUrl);
    if (result.error) {
      setConfig({
        kind: 'error',
        message:
          'Could not load the card payment setup — the card was not charged.',
      });
      return;
    }
    const bootstrap = asAdminAcceptJsConfig(result.data);
    if (!bootstrap) {
      setConfig({ kind: 'not-configured' });
      return;
    }
    setConfig({ kind: 'ready', bootstrap });
  }, [apiUrl]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // Warm the Accept.js script once the mode is known so the submit path
  // usually skips the network wait. A warm-up failure is NOT terminal —
  // the submit handler re-attempts (the loader clears its cache on
  // error) and only then surfaces the unavailable panel.
  useEffect(() => {
    if (config.kind !== 'ready') return;
    loadAcceptJs(config.bootstrap.mode).catch(() => {
      /* retried on submit */
    });
  }, [config]);

  /**
   * PAN/CVV hygiene: card data must not linger in mounted React state
   * once it is no longer needed — called the moment dispatchData hands
   * the fields to Accept.js (the nonce replaces them for every later
   * outcome, including a decline: the form re-arms empty).
   */
  const clearCardFields = (): void => {
    setCardNumber('');
    setExpiry('');
    setCvv('');
    setName('');
    setZip('');
  };

  const cardDigits = cardNumber.replace(/\D/g, '');
  const expiryDigits = expiry.replace(/\D/g, '');
  const expiryMonth = expiryDigits.slice(0, 2);
  const expiryYear = expiryDigits.slice(2);
  const monthValid =
    expiryMonth.length === 2 &&
    parseInt(expiryMonth, 10) >= 1 &&
    parseInt(expiryMonth, 10) <= 12;
  const isFormValid =
    cardDigits.length >= 13 &&
    cardDigits.length <= 19 &&
    monthValid &&
    expiryYear.length === 2 &&
    cvv.length >= 3 &&
    name.trim().length > 0;

  const handleCharge = useCallback(async (): Promise<void> => {
    if (config.kind !== 'ready' || !isFormValid || flow.kind === 'processing') {
      return;
    }
    setFlow({ kind: 'processing' });
    setFormError(null);

    // 1. Script (lazy, by resolved tenant mode — never hardcoded).
    let accept: AcceptStatic;
    try {
      accept = await loadAcceptJs(config.bootstrap.mode);
    } catch {
      setFlow({
        kind: 'terminal',
        panel: {
          kind: 'unavailable',
          message:
            'The secure card library could not load — the card was NOT charged. Check the connection, refresh, and reopen this payment.',
        },
      });
      return;
    }

    // 2. Tokenize in the browser. Card data goes into dispatchData and
    //    NOWHERE else.
    let opaqueData;
    try {
      opaqueData = await dispatchCardData(
        accept,
        {
          apiLoginID: config.bootstrap.apiLoginID,
          clientKey: config.bootstrap.publicClientKey,
        },
        {
          cardNumber: cardDigits,
          month: expiryMonth,
          year: `20${expiryYear}`,
          cardCode: cvv,
          ...(zip.trim() !== '' ? { zip: zip.trim() } : {}),
          ...(name.trim() !== '' ? { fullName: name.trim() } : {}),
        },
      );
    } catch (e) {
      if (e instanceof AcceptJsDispatchError) {
        const info = describeAcceptJsError(e.errors[0]?.code ?? '');
        if (info.kind === 'config') {
          // Broken tenant setup (E_WC_21 bad creds / E_WC_02 no HTTPS) —
          // not fixable at the counter. No charge happened.
          setFlow({
            kind: 'terminal',
            panel: { kind: 'unavailable', message: info.message },
          });
          return;
        }
        setFormError(info);
      } else {
        setFormError({
          kind: 'transient',
          message:
            'Something went wrong preparing the card details. No charge was made — please try again.',
        });
      }
      setFlow({ kind: 'idle' });
      return;
    }

    // Tokenization succeeded — the nonce now stands in for the card.
    // Wipe the PAN/CVV from React state immediately; every outcome
    // below proceeds without card data in memory.
    clearCardFields();

    // 3. One-shot charge. Fresh key per submit (see header); the server
    // recalculates the total from the booking row + tipCents.
    //
    // #522 review — `expectedTotalCents` is the amount THIS FORM showed
    // the customer. The server never charges it; it compares and refuses
    // before touching the gateway. Without it, an applied redemption
    // (which reduces the on-screen total but not the booking row) would
    // capture the full price while the button promised less.
    const result = await adminPaymentsApi.chargeManualCard(apiUrl, {
      bookingId,
      tipCents,
      idempotencyKey: makeIdempotencyKey('manual', bookingId),
      opaqueData,
      allowAdditionalCharge: false,
      expectedTotalCents: totalCents + tipCents,
    });
    if (result.data) {
      onSuccess(result.data.paymentId);
      return;
    }
    const failure = classifyChargeFailure(result);
    if (failure.kind === 'inline') {
      setFormError({ kind: 'transient', message: failure.message });
      setFlow({ kind: 'idle' });
      return;
    }
    setFlow({ kind: 'terminal', panel: failure.panel });
  }, [
    apiUrl,
    bookingId,
    tipCents,
    config,
    flow.kind,
    isFormValid,
    cardDigits,
    expiryMonth,
    expiryYear,
    cvv,
    name,
    zip,
    onSuccess,
  ]);

  const totalWithTip = totalCents + tipCents;
  const processing = flow.kind === 'processing';

  return (
    <section
      className="ferro-card"
      aria-label="Keyed card entry"
      data-testid="acceptjs-keyed-entry"
    >
      <header className="ferro-card__header">
        <h3 className="ferro-section__subtitle">Manual card entry</h3>
        <span
          className="ferro-badge ferro-badge--neutral"
          data-testid="keyed-total"
        >
          {formatMoney(totalWithTip)}
        </span>
      </header>

      {config.kind === 'loading' ? (
        <p className="ferro-muted" role="status" data-testid="keyed-config-loading">
          Loading secure card entry…
        </p>
      ) : null}

      {config.kind === 'error' ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="keyed-config-error"
        >
          <p>{config.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void loadConfig()}
            data-testid="keyed-config-retry"
          >
            Retry
          </button>
        </div>
      ) : null}

      {config.kind === 'not-configured' ? (
        <div data-testid="keyed-not-configured">
          <p>Card payment setup for this facility isn&rsquo;t finished.</p>
          <p className="ferro-muted">
            An owner can complete it under Payments → Gateway credentials.
            Until then, collect another way (card on file, or settle later).
          </p>
        </div>
      ) : null}

      {config.kind === 'ready' && flow.kind === 'terminal' ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          aria-live="polite"
          data-testid={`keyed-terminal-${flow.panel.kind}`}
        >
          <p>
            <strong>{terminalTitle(flow.panel.kind)}</strong>
          </p>
          <p>{flow.panel.message}</p>
          {flow.panel.heldTransactionId ? (
            <p data-testid="keyed-held-transaction-id">
              Authorize.net transaction:{' '}
              <code>{flow.panel.heldTransactionId}</code>
            </p>
          ) : null}
        </div>
      ) : null}

      {config.kind === 'ready' && flow.kind !== 'terminal' ? (
        <div className="ferro-form" data-testid="keyed-card-form">
          <p className="ferro-muted">
            Card details are encrypted in this browser — they never touch
            our servers.
          </p>
          {/* Part of the /manual/charge contract: keyed cards settle at
              card-not-present rates — the desk should prefer tap/reader
              rails where they exist. */}
          <p className="ferro-muted" data-testid="keyed-cnp-disclosure">
            Hand-keyed cards are charged at card-not-present rates.
          </p>

          {formError ? (
            <p className="ferro-form__error" role="alert" data-testid="keyed-error">
              {formError.message}
            </p>
          ) : null}

          <label htmlFor="keyed-card-number">
            Card number
            <input
              id="keyed-card-number"
              type="text"
              inputMode="numeric"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="1234 5678 9012 3456"
              autoComplete="cc-number"
              disabled={processing}
              aria-invalid={formError?.field === 'cardNumber' || undefined}
              data-testid="keyed-card-number"
              style={{
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '0.06em',
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 'var(--ferro-space-3, 12px)' }}>
            <label htmlFor="keyed-expiry" style={{ flex: 1 }}>
              Expiry
              <input
                id="keyed-expiry"
                type="text"
                inputMode="numeric"
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                placeholder="MM/YY"
                autoComplete="cc-exp"
                disabled={processing}
                aria-invalid={formError?.field === 'expiry' || undefined}
                data-testid="keyed-expiry"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              />
            </label>
            <label htmlFor="keyed-cvv" style={{ flex: 1 }}>
              CVC
              <input
                id="keyed-cvv"
                type="password"
                inputMode="numeric"
                value={cvv}
                onChange={(e) =>
                  setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                placeholder="123"
                autoComplete="cc-csc"
                disabled={processing}
                aria-invalid={formError?.field === 'cvv' || undefined}
                data-testid="keyed-cvv"
              />
            </label>
          </div>
          <label htmlFor="keyed-name">
            Cardholder name
            <input
              id="keyed-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name as it appears on the card"
              autoComplete="cc-name"
              disabled={processing}
              aria-invalid={formError?.field === 'name' || undefined}
              data-testid="keyed-name"
            />
          </label>
          <label htmlFor="keyed-zip">
            Billing ZIP <span className="ferro-muted">(optional)</span>
            <input
              id="keyed-zip"
              type="text"
              inputMode="numeric"
              value={zip}
              onChange={(e) =>
                setZip(e.target.value.replace(/[^\d-]/g, '').slice(0, 10))
              }
              placeholder="80202"
              autoComplete="postal-code"
              disabled={processing}
              aria-invalid={formError?.field === 'zip' || undefined}
              data-testid="keyed-zip"
            />
          </label>

          <div className="ferro-form__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => void handleCharge()}
              disabled={!isFormValid || processing}
              data-testid="keyed-charge-button"
            >
              {processing ? 'Charging…' : `Charge ${formatMoney(totalWithTip)}`}
            </button>
          </div>
          {processing ? (
            <p className="ferro-muted" role="status" data-testid="keyed-status">
              Charging card…
            </p>
          ) : null}
        </div>
      ) : null}

      <footer className="ferro-card__footer">
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onCancel}
          disabled={processing}
          data-testid="keyed-cancel-button"
        >
          Back
        </button>
      </footer>
    </section>
  );
}
